<template>
    <div class="conifer">

        <!-- NB: we don't wrap the whole coniferForm with the spinner, this would destroy the form! -->
        <spinner-until :condition="!submitting"/>

        <survey-form :survey-id="surveyId"
                     :for-conifer="true"
                     @exit="onExit()"/>
        <form ref="coniferForm" :action="postUrl" method="POST" style="visibility: hidden; height: 0; overflow: hidden">
            <b-row>
                <b-col>
                    <div v-for="(scoreName, i) in scoreNames" :key="i">
                        <label>{{ scoreName }}</label>
                        <input type="text" :name="scoreName"
                               :value="scores[scoreName]"/>
                    </div>
                </b-col>
                <b-col>
                    <label>imdata</label>
                    <textarea name="imdata" :value="scoreString"></textarea>
                </b-col>
                <b-col>
                    <label>caseIndex</label>
                    <input name="caseIndex" type="text" :value="caseIndex"/>
                    <hr/>
                    <input type="submit"/>
                </b-col>
                <b-col>
                    <label>returnUrl</label>
                    <br/>
                    <pre>{{ returnUrl }}</pre>
                </b-col>
            </b-row>
        </form>
    </div>
</template>

<script>

import Vue from 'vue'
import PracticeFields from '@/models/Practice'

/**
 * Main front-end component to display the survey form for the Conifer portal.
 * Currently loads data submitted from the ConiferTest component.
 */

export default Vue.extend({
    name: "Conifer",
    props: {
        participantId: Number,
        surveyId: Number,
        returnUrl: String,
        error: String,
        caseIndex: Number,
    },
    data() {
        // NB: calling $api.inLocalDev is not reliable here! (??)
        const inLocalDev = process.env.VUE_APP_API_HOST === undefined

        return {
            submitting: false,

            postUrl: inLocalDev ? "http://wcd-api.test?r=conifer/score-test" : this.returnUrl,

            // see also ConiferService::getScoresForConifer
            scoreNames: [
                'KEY',
                'PARTICIPANT_ID',
                'EXTERNAL_PLAN_ID',
                'PARTICIPANT_RESEARCH_OPTIN',
                'SURVEY_ID',
                'EXTERNAL_SURVEY_ID',
                'TOTAL_SCORE',
                'TOTAL_SCORE_THRESHOLDS',

                'FRAMINGHAM_SCORE',
                'SCORE_BMI',
                'SCORE_BMI_THRESHOLDS',
                'SCORE_BMI_HIGHER_IS_BETTER',
                'SCORE_CHOL',
                'SCORE_CHOL_THRESHOLDS',
                'SCORE_CHOL_HIGHER_IS_BETTER',
                'SCORE_BP',
                'SCORE_BP_THRESHOLDS',
                'SCORE_BP_HIGHER_IS_BETTER',
                'SCORE_METAB',
                'SCORE_METAB_THRESHOLDS',
                'SCORE_METAB_HIGHER_IS_BETTER',
                'SCORE_PMH',
                'SCORE_PMH_THRESHOLDS',
                'SCORE_PMH_HIGHER_IS_BETTER',
                'SCORE_SMOKE',
                'SCORE_SMOKE_THRESHOLDS',
                'SCORE_SMOKE_HIGHER_IS_BETTER',
                'SCORE_EXER',
                'SCORE_EXER_THRESHOLDS',
                'SCORE_EXER_HIGHER_IS_BETTER',
                'SCORE_EXER_2',
                'SCORE_EXER_2_THRESHOLDS',
                'SCORE_EXER_2_HIGHER_IS_BETTER',
                'SCORE_SED',
                'SCORE_SED_THRESHOLDS',
                'SCORE_SED_HIGHER_IS_BETTER',
                'SCORE_STRESS',
                'SCORE_STRESS_THRESHOLDS',
                'SCORE_STRESS_HIGHER_IS_BETTER',
                'SCORE_DIET',
                'SCORE_DIET_THRESHOLDS',
                'SCORE_DIET_HIGHER_IS_BETTER',

                'BIO_BP_SYS',
                'BIO_BP_SYS_THRESHOLDS',
                'BIO_BP_SYS_HIGHER_IS_BETTER',
                'BIO_BP_DIA',
                'BIO_BP_DIA_THRESHOLDS',
                'BIO_BP_DIA_HIGHER_IS_BETTER',
                'BIO_BMI',
                'BIO_BMI_THRESHOLDS',
                'BIO_BMI_HIGHER_IS_BETTER',
                'BIO_LDL_NO_CVD',
                'BIO_LDL_NO_CVD_THRESHOLDS',
                'BIO_LDL_NO_CVD_HIGHER_IS_BETTER',
                'BIO_TC_NO_CVD',
                'BIO_TC_NO_CVD_THRESHOLDS',
                'BIO_TC_NO_CVD_HIGHER_IS_BETTER',
                'BIO_LDL_CVD_OR_DM',
                'BIO_LDL_CVD_OR_DM_THRESHOLDS',
                'BIO_LDL_CVD_OR_DM_HIGHER_IS_BETTER',
                'BIO_TC_CVD_OR_DM',
                'BIO_TC_CVD_OR_DM_THRESHOLDS',
                'BIO_TC_CVD_OR_DM_HIGHER_IS_BETTER',
                'BIO_HDL_MEN',
                'BIO_HDL_MEN_THRESHOLDS',
                'BIO_HDL_MEN_HIGHER_IS_BETTER',
                'BIO_HDL_WOMEN',
                'BIO_HDL_WOMEN',
                'BIO_HDL_WOMEN_THRESHOLDS',
                'BIO_HDL_WOMEN_HIGHER_IS_BETTER',
                'BIO_TG',
                'BIO_TG_THRESHOLDS',
                'BIO_TG_HIGHER_IS_BETTER',
                'BIO_TC_OVER_HDL',
                'BIO_TC_OVER_HDL_THRESHOLDS',
                'BIO_TC_OVER_HDL_HIGHER_IS_BETTER',
                'BIO_FBG',
                'BIO_FBG_THRESHOLDS',
                'BIO_FBG_HIGHER_IS_BETTER',
                'BIO_A1C',
                'BIO_A1C_THRESHOLDS',
                'BIO_A1C_HIGHER_IS_BETTER',
                'BIO_WAIST_MEN',
                'BIO_WAIST_MEN_THRESHOLDS',
                'BIO_WAIST_MEN_HIGHER_IS_BETTER',
                'BIO_WAIST_WOMEN',
                'BIO_WAIST_WOMEN_THRESHOLDS',
                'BIO_WAIST_WOMEN_HIGHER_IS_BETTER',
                'WL_SEVERE_OBESITY',
                'WL_HDL_LOW',
                'WL_LDL_HIGH',
                'WL_BP_HIGH',
                'WL_A1C_ELEVATED',
                'WL_METAB_SYNDROME',
                'WL_VERY_HIGH_RISK',
                'WL_QUIT_SMOKING',
                'WL_TOO_SEDENTARY',
                'WL_STRESS',
                'WL_EAT_MORE_VEGGIES',
                'WL_POOR_DIET',

                'SCORE_BP_ADVICE',
                'SCORE_PMH_ADVICE',
                'SCORE_SMOKE_ADVICE',
                'SCORE_EXER_ADVICE',
                'SCORE_STRESS_ADVICE',
                'SCORE_DIET_ADVICE',
                'BIO_BMI_ADVICE',
                'BIO_LDL_NO_CVD_ADVICE',
                'BIO_TC_NO_CVD_ADVICE',
                'BIO_LDL_CVD_OR_DM_ADVICE',
                'BIO_TC_CVD_OR_DM_ADVICE',
                'BIO_HDL_MEN_ADVICE',
                'BIO_HDL_WOMEN_ADVICE',
                'BIO_TG_ADVICE',
                'BIO_FBG_ADVICE',
                'BIO_A1C_ADVICE',
                'BIO_WAIST_MEN_ADVICE',
                'BIO_WAIST_WOMEN_ADVICE',
                'TOTAL_SCORE_ADVICE',

                'EXIT_METHOD',

                'RESPONSE_PMH_CVD',
                'RESPONSE_PMH_HTN',
                'RESPONSE_PMH_CHOL',
                'RESPONSE_PMH_DM',
                'RESPONSE_MEDS',
                'RESPONSE_ANTHROPOMETRICS',
                'RESPONSE_LAB_TESTS_CHOL',
                'RESPONSE_LAB_TESTS_GLUCOSE',

                'RESPONSE_SMOKE_STAT',
                'RESPONSE_SMOKE_QUIT',
                'RESPONSE_SMOKE_CIGS',
                'RESPONSE_PA_MOD_FREQ',
                'RESPONSE_PA_MOD_DUR',
                'RESPONSE_PA_HI_FREQ',
                'RESPONSE_PA_HI_DUR',
                'RESPONSE_SED_HRS',
                'RESPONSE_SED_VIDEO',
                'RESPONSE_SED_COMPUTER',
                'RESPONSE_WORRIES',
                'RESPONSE_HECTIC',
                'RESPONSE_NO_TIME',
                'RESPONSE_COMPETE',
                'RESPONSE_AUTONOMY',
                'RESPONSE_NERVOUS',
                'RESPONSE_ANGER',
                'RESPONSE_ARGUES',
                'RESPONSE_ANNOYED',
                'RESPONSE_SUPPRESSED',
                'RESPONSE_TEMPER',
                'RESPONSE_PLEASURE',
                'RESPONSE_ANXIETY',
                'RESPONSE_DEPRESSED',
                'RESPONSE_VEGGIES',
                'RESPONSE_FRUITS',
                'RESPONSE_WW_GRAINS',
                'RESPONSE_LF_DAIRY',
                'RESPONSE_FF_DAIRY',
                'RESPONSE_EGGS',
                'RESPONSE_BEANS',
                'RESPONSE_SEAFOOD',
                'RESPONSE_POULTRY',
                'RESPONSE_MEAT',
                'RESPONSE_SNACKS',
                'RESPONSE_FRIED',
                'RESPONSE_PASTRY',
                'RESPONSE_EAT_OUT',
                'RESPONSE_HEALTH_FOOD',
                'RESPONSE_TYPE_OF_FAT',
                'RESPONSE_COOKING_FATS',
                'RESPONSE_TABLE_FATS',
            ],

            scores: []
        }
    },
    computed: {
        scoreString() {
            return JSON.stringify(this.scores)
        }
    },
    methods: {
        // triggered by Save and Exit button on SurveyForm
        async onExit() {
            this.submitting = true
            this.scores = await this.$api.getConiferScores(this, this.participantId, this.surveyId)
            console.log(this.scores)
            Vue.nextTick(() => {
                this.$refs.coniferForm.submit()
            })
        },
    },
})

</script>

